import { useGameBoardLayout } from "@/game/hooks/useGameBoardLayout";
import "@/game/styles/board.css";
import { type CheckerMovement, GamePlayAnimationType } from "@/game/utils/animationTypes";
import { CounterDisplayType } from "@/game/utils/checkerDisplayUtils";
import { GameLogger } from "@/game/utils/gameLogger.ts";
import { calculateDuration, detectCheckerMovements } from "@/game/utils/movementDetection";
import {
  applyCheckerMovementEnd,
  applyCheckerMovementStart
} from "@/game/utils/movementStateUtils";
import { playSound } from "@/lib/audio";
import type { AtomicMove, GameState, PlayerColor } from "@nextgammon/shared";
import { Constants, Game, Rules } from "@nextgammon/shared";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import type { DieState } from "../hooks/useGamePlayState";
import { DoublingCubePixi } from "./DoublingCubePixi";
import { GameBoardBar } from "./GameBoardBar";
import { GameBoardDebugOverlays } from "./GameBoardDebugOverlays";
import { GameBoardPoint } from "./GameBoardPoint.tsx";
import { GameCheckerOffArea } from "./GameCheckerOffArea";
import { GameDice } from "./GameDice";

import { GameBoardOverlayContext } from "@/game/context/GameBoardOverlayContext";
import { Sprite, Stage } from "@pixi/react";
import { Easing, Tween } from "@tweenjs/tween.js";
import * as PIXI from "pixi.js";
import { Texture } from "pixi.js";

export enum GameBoardState {
  Loading = "Loading",
  PreDiceRoll = "PreDiceRoll",
  DiceRolling = "DiceRolling",
  Idle = "Idle",
  CheckerActions = "CheckerActions"
}

interface MovePath {
  // Ordered sequence of moves from the original selected point
  moves: AtomicMove[];
}

interface GameBoardProps {
  gameState: GameState | null;
  gameMode: string; // Static configuration, separate from dynamic details
  gameDetails: {
    player1Id: string;
    player2Id: string;
    player1Username?: string;
    player2Username?: string;
    cubeValue?: number;
    cubeOwner?: string | null;
    doublingAllowed?: boolean;
  };
  currentUserId: string;
  isPlayerTurn: boolean;
  currentDice: DieState[];
  whiteDice: DieState[];
  blackDice: DieState[];
  diceRolled: boolean;
  onAttemptMove: (move: AtomicMove) => Promise<boolean>;
  onOfferDouble?: () => void;
  canOfferDouble?: boolean;
  animatingState: number;
  onAnimationStepComplete: (animation: GamePlayAnimationType) => void;
  localTurnHistory: Array<{
    gameStateSnapshot: GameState;
    moveMade: AtomicMove;
  }>;
  gameOver?: boolean;
  winnerInfo?: { id: string; color: PlayerColor } | null;
}

export function GameBoard({
  gameState,
  gameMode,
  gameDetails,
  currentUserId,
  isPlayerTurn,
  currentDice,
  whiteDice,
  blackDice,
  diceRolled,
  onAttemptMove,
  onOfferDouble,
  canOfferDouble = false,
  animatingState,
  onAnimationStepComplete,
  localTurnHistory,
  gameOver = false,
  winnerInfo = null
}: GameBoardProps) {
  const overlay = useContext(GameBoardOverlayContext);
  const parentRef = useRef<HTMLDivElement | null>(null);
  const animationInProgressRef = useRef<boolean>(false);
  const currentPlayerIdRef = useRef<string | null>(null);
  const pendingMoveQueueRef = useRef<AtomicMove[]>([]);
  const lastAppliedMovesCountRef = useRef<number>(0);
  const movementQueueRef = useRef<CheckerMovement[]>([]);
  const currentMovementIndexRef = useRef<number>(0);
  const movementTweenDelay = useRef<number>(0);

  const [parentRect, setParentRect] = useState<DOMRect | null>(null);
  const [boardTexture, setBoardTexture] = useState<Texture | null>(null);
  const [sampleCheckerTexture, setSampleCheckerTexture] = useState<Texture | null>(null);
  const [boardState, setBoardState] = useState<GameBoardState>(GameBoardState.Loading);

  const [displayGameState, setDisplayGameState] = useState<GameState | null>(gameState);

  const [selectedPointIndex, setSelectedPointIndex] = useState<number | null>(null);
  const [potentialMovesSingle, setPotentialMovesSingle] = useState<number[]>([]);
  const [potentialMovePaths, setPotentialMovePaths] = useState<MovePath[]>([]);
  const [potentialMovesCombo, setPotentialMovesCombo] = useState<
    { first: number; second: number }[]
  >([]);
  const prevGameStateRef = useRef<{ gameState: GameState | null; localMoveCount: number }>({
    gameState: null,
    localMoveCount: 0
  });
  const [isDiceRollInitialActive, setIsDiceRollInitialActive] = useState(false);
  const [isDiceRollInterstitialActive, setIsDiceRollInterstitialActive] = useState(false);

  function updatePrevGameStateRef(gameState: GameState | null, localHistoryLength: number) {
    prevGameStateRef.current = {
      gameState: structuredClone(gameState),
      localMoveCount: localHistoryLength
    };
  }

  // Handle window resize to ensure the Pixi canvas resizes correctly
  useEffect(() => {
    if (!parentRef.current) return;

    GameLogger.logInfo("GameBoard", "Parent size changed, updating parentRect");

    const updateRect = () => {
      if (parentRef.current) {
        const newRect = parentRef.current!.getBoundingClientRect();
        setParentRect(newRect);
      }
    };

    updateRect();

    const resizeObserver = new ResizeObserver(updateRect);
    resizeObserver.observe(parentRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  // Load the board texture and a sample checker texture
  useEffect(() => {
    // Just loading the default board texture for now
    const texture = PIXI.Assets.get("board");
    if (texture.baseTexture.valid) {
      setBoardTexture(texture);
    } else {
      texture.baseTexture.once("loaded", () => setBoardTexture(texture));
    }

    // Load a sample checker texture to get its unscaled height
    const checkerTex = PIXI.Assets.get("board-checker-local");
    if (checkerTex.baseTexture.valid) {
      setSampleCheckerTexture(checkerTex);
    } else {
      checkerTex.baseTexture.once("loaded", () => setSampleCheckerTexture(checkerTex));
    }
  }, []);
  // Load the board background texture
  const boardBGTexture = useMemo(() => PIXI.Assets.get("board-bg"), []);

  const playerColor =
    currentUserId === gameDetails.player1Id ? Constants.PLAYER_WHITE : Constants.PLAYER_BLACK;
  // Flip the board presentation so that the player's home is always in the lower right corner
  const isFlipped = playerColor === Constants.PLAYER_BLACK;

  // Calculate board layout related values
  // Apply a slight scale down to ensure it fits within the parent container
  const {
    stageWidth,
    stageHeight,
    boardScale,
    playArea,
    bar1Area,
    bar2Area,
    bearOff1Area,
    bearOff2Area,
    diceArea,
    boardDimensions,
    getPointArea
  } = useGameBoardLayout(parentRect, boardTexture, 0.925, sampleCheckerTexture, isFlipped);

  const boardBGScale = Math.max(
    stageWidth / (boardBGTexture?.width || 1),
    stageHeight / (boardBGTexture?.height || 1)
  );

  // Provide the play area to the overlay context
  useEffect(() => {
    if (overlay?.setPlayArea) {
      GameLogger.logDebug("GameBoard", `Setting play area: ${playArea}`);

      overlay.setPlayArea({
        x: playArea[0],
        y: playArea[1],
        width: playArea[2],
        height: playArea[3]
      });
    }
  }, [playArea, overlay]);

  const isAnimating = !!animatingState && animatingState !== 0;
  // Safely access checker counts, defaulting to 0 if bar or counts are null/undefined
  const whiteBarCount = (displayGameState && displayGameState.bar?.white) || 0;
  const blackBarCount = (displayGameState && displayGameState.bar?.black) || 0;
  // Determine if the local player can select a checker to move
  const canSelectChecker = () =>
    !gameOver && isPlayerTurn && diceRolled && currentDice.some((d) => !d.used) && !isAnimating;

  const changeBoardState = (state: GameBoardState) => {
    GameLogger.logInfo(
      "GameBoard",
      `setBoardState called with: ${state} for player color: ${gameState?.currentPlayerColor}`
    );
    setBoardState(state);
  };

  // Board state management
  useEffect(() => {
    if (boardState === GameBoardState.Loading) {
      if (boardTexture && sampleCheckerTexture && parentRect && overlay && gameState) {
        changeBoardState(GameBoardState.PreDiceRoll);
      }
      return;
    }
    if (boardState === GameBoardState.PreDiceRoll) {
      if (gameState) {
        currentPlayerIdRef.current = gameState.currentPlayerId;
      }

      if (animatingState & GamePlayAnimationType.State) {
        // Clear the initial animation state once ready
        onAnimationStepComplete(GamePlayAnimationType.State);
      }

      // Wait to progress if the game hasn't started
      const gameStarted = gameState && gameState.sequence !== null && gameState.sequence >= 0;

      // For initial roll (sequence 0), we should have dice already
      const hasInitialRollDice =
        gameState && gameState.sequence === 1 && gameState.dice && gameState.dice.length > 0;

      GameLogger.logInfo(
        "GameBoard",
        `PreDiceRoll state: gameStarted=${gameStarted}, playerHasDice=${diceRolled}, hasInitialRollDice=${hasInitialRollDice}, sequence=${gameState?.sequence}`
      );

      if (gameStarted && diceRolled) {
        if (hasInitialRollDice) {
          if (!isDiceRollInitialActive /*&& animatingState & GamePlayAnimationType.Dice*/) {
            GameLogger.logInfo("GameBoard", "Dice roll initial animation active, waiting..");
            setIsDiceRollInitialActive(true);
            setIsDiceRollInterstitialActive(false);
            // Set tween delay for the opponent's first move just to make it easier to see
            if (!isPlayerTurn) {
              movementTweenDelay.current = 1500;
            }
          }
          changeBoardState(GameBoardState.DiceRolling);
        } else {
          if (!isDiceRollInterstitialActive && animatingState & GamePlayAnimationType.Dice) {
            GameLogger.logInfo("GameBoard", "Dice roll interstitial animation active, waiting..");
            setIsDiceRollInterstitialActive(true);
            setIsDiceRollInitialActive(false);
            // Set tween delay for the opponent's first move just to make it easier to see
            if (!isPlayerTurn) {
              movementTweenDelay.current = 1500;
            }
          }
          changeBoardState(GameBoardState.DiceRolling);
        }
      }
      return;
    }
    if (boardState === GameBoardState.DiceRolling) {
      // If the interstitial is active, we wait for it to complete
      if (!isDiceRollInitialActive && !isDiceRollInterstitialActive) {
        onAnimationStepComplete(GamePlayAnimationType.Dice);
        // Otherwise, we can proceed to checker actions
        changeBoardState(GameBoardState.Idle);
      }
      return;
    }
    if (boardState === GameBoardState.Idle) {
      if (gameState && sampleCheckerTexture) {
        // For initial roll dice, ensure we're properly initialized
        if (gameState.sequence === 0 && !prevGameStateRef.current.gameState) {
          GameLogger.logInfo(
            "GameBoard",
            "In Idle state with initial roll dice - initializing prevGameStateRef"
          );
          updatePrevGameStateRef(gameState, 0);
        }

        // Check if the current player has changed (but not for the very first turn with initial roll dice)
        if (
          gameState &&
          currentPlayerIdRef.current &&
          gameState.currentPlayerId !== currentPlayerIdRef.current &&
          !(gameState.sequence === 0 && gameState.dice && gameState.dice.length > 0)
        ) {
          GameLogger.logInfo(
            "GameBoard",
            `Player changed from ${currentPlayerIdRef.current} to ${gameState.currentPlayerId}`
          );

          changeBoardState(GameBoardState.PreDiceRoll);
        }

        GameLogger.logInfo(
          "GameBoard",
          `Idle state: checking for movements and animations, isAnimating=${animatingState & GamePlayAnimationType.Checker}, inProgress=${animationInProgressRef.current}`
        );

        // Auto-select checkers on the board (for the local player)
        if (canSelectChecker()) {
          attemptBarSelect();
        }

        const checkerAnimation = animatingState & GamePlayAnimationType.Checker;
        if (checkerAnimation && !animationInProgressRef.current) {
          animationInProgressRef.current = true;

          GameLogger.logInfo(
            "GameBoard",
            `Undo detection: localTurnHistory length: ${localTurnHistory?.length} previous game state moves length: ${prevGameStateRef.current.localMoveCount}`
          );

          let isUndo = false;
          if (
            prevGameStateRef.current.gameState &&
            gameState.moves.length < prevGameStateRef.current.gameState.moves.length
          ) {
            isUndo = true;
          }
          if (
            localTurnHistory &&
            localTurnHistory.length < prevGameStateRef.current.localMoveCount
          ) {
            isUndo = true;
          }
          const movements = !boardDimensions
            ? []
            : detectCheckerMovements(
                prevGameStateRef.current.gameState,
                gameState,
                boardDimensions,
                isUndo
              );

          GameLogger.logInfo(
            "GameBoard",
            `Detected ${movements.length} movements, isUndo: ${isUndo}, overlay: ${!!overlay}`
          );

          if (movements.length > 0) {
            currentMovementIndexRef.current = 0;
            movementQueueRef.current = movements;

            changeBoardState(GameBoardState.CheckerActions);
          } else {
            // No movements, ensure final state is shown
            setDisplayGameState(gameState);
            animationInProgressRef.current = false;
            updatePrevGameStateRef(gameState, localTurnHistory ? localTurnHistory.length : 0);
            onAnimationStepComplete(GamePlayAnimationType.Checker);
          }
        } else if (!checkerAnimation) {
          // Ensure displayGameState is up-to-date when not animating
          setDisplayGameState(gameState);
          if (
            !prevGameStateRef.current.gameState ||
            prevGameStateRef.current.gameState.sequence !== gameState.sequence
          ) {
            GameLogger.logInfo(
              "GameBoard",
              "No animation in progress, updating displayGameState and prevGameStateRef"
            );
            // Update prevGameStateRef if it's null or if the game sequence has changed,
            // ensuring it's ready for the next animation comparison.
            updatePrevGameStateRef(gameState, localTurnHistory ? localTurnHistory.length : 0);
          }
        }
      }
      return;
    }
    if (boardState === GameBoardState.CheckerActions) {
      if (
        movementQueueRef.current.length === 0 ||
        currentMovementIndexRef.current >= movementQueueRef.current.length
      ) {
        GameLogger.logInfo("GameBoard", "All movements completed");

        movementQueueRef.current = [];
        currentMovementIndexRef.current = 0;

        changeBoardState(GameBoardState.Idle);
      }
    }
  }, [
    overlay,
    parentRect,
    animatingState,
    isPlayerTurn,
    onAnimationStepComplete,
    boardTexture,
    sampleCheckerTexture,
    currentUserId,
    boardState,
    gameState,
    displayGameState,
    whiteDice,
    blackDice,
    isDiceRollInitialActive,
    isDiceRollInterstitialActive
  ]);

  // Configure board interstitials and checker animations
  useEffect(() => {
    if (!gameState || !overlay || boardState !== GameBoardState.CheckerActions) return;
    const animateNext = () => {
      if (currentMovementIndexRef.current >= movementQueueRef.current.length) {
        GameLogger.logInfo("GameBoard", "All movements completed (animateNext)");

        setDisplayGameState(gameState);
        animationInProgressRef.current = false;
        updatePrevGameStateRef(gameState, localTurnHistory ? localTurnHistory.length : 0);
        onAnimationStepComplete(GamePlayAnimationType.Checker);
        return;
      }

      GameLogger.logInfo(
        "GameBoard",
        `Animating movement ${currentMovementIndexRef.current + 1} of ${movementQueueRef.current.length}`
      );

      const movement = movementQueueRef.current[currentMovementIndexRef.current];
      const duration = calculateDuration(
        movement.fromPosition,
        movement.toPosition,
        gameMode,
        isPlayerTurn,
        movement.isReverse
      );

      // Create the sprite
      const checkerTexture =
        movement.playerColor === Constants.PLAYER_WHITE
          ? PIXI.Assets.get("board-checker-local")
          : PIXI.Assets.get("board-checker-opponent");
      const sprite = new PIXI.Sprite(checkerTexture);
      sprite.anchor.set(0.5);
      sprite.x = movement.fromPosition.x;
      sprite.y = movement.fromPosition.y;
      sprite.scale.set(boardScale);

      // Create the tween to move the checker
      const tween = new Tween(sprite)
        // Set the target position relative to the play area
        .to(
          {
            x: movement.toPosition.x - playArea[0],
            y: movement.toPosition.y - playArea[1]
          },
          duration
        )
        .easing(Easing.Quadratic.InOut)
        // Add a slight delay for the first opponent move
        .delay(movementTweenDelay.current)
        .onStart(() => {
          // Play checker move sound
          playSound("checker-move");

          // Update displayGameState to reflect the start of the move
          setDisplayGameState((prev) => {
            // Remove checker from source
            return applyCheckerMovementStart(prev, movement);
          });
          // Reset the tween delay for subsequent movements
          movementTweenDelay.current = 0;
        })
        .onComplete(() => {
          GameLogger.logInfo(
            "GameBoard",
            `Tween complete for movement ${currentMovementIndexRef.current + 1}`
          );

          // Update displayGameState to reflect the completed move
          setDisplayGameState((prev) => {
            // Add checker to destination
            return applyCheckerMovementEnd(prev, movement);
          });

          overlay.removeSprite(`checker-move-${currentMovementIndexRef.current}`, 100);
          ++currentMovementIndexRef.current;

          // Continue with next animation or finish
          animateNext();
        });

      // Add sprite and tween to overlay
      overlay.addSpriteWithTween(
        `checker-move-${currentMovementIndexRef.current}`,
        sprite,
        tween,
        true
      );
    };

    animateNext();
  }, [boardState]);

  // Top-level guard for gameState
  if (!gameState || !displayGameState) {
    return <div className="p-4 text-center">Waiting for game state...</div>;
  }

  const resetPotentialMoves = () => {
    setPotentialMovesSingle([]);
    setPotentialMovesCombo([]);
    setPotentialMovePaths([]);
  };

  // Reset selected point and potential moves when the player changes or dice are rolled
  useEffect(() => {
    setSelectedPointIndex(null);
    resetPotentialMoves();
  }, [gameState.currentPlayerId, gameState.dice]);

  // Determine potential moves based on the selected point and current dice
  useEffect(() => {
    if (
      selectedPointIndex === null ||
      !isPlayerTurn ||
      !currentDice ||
      currentDice.length === 0 ||
      (currentDice && currentDice.length > 0 && currentDice.every((d) => d.used)) ||
      isAnimating
    ) {
      resetPotentialMoves();
      return;
    }

    const unusedDice = currentDice.filter((d) => !d.used);
    if (unusedDice.length === 0) {
      resetPotentialMoves();
      return;
    }

    const paths: MovePath[] = [];

    function findPaths(
      state: GameState,
      fromPoint: number,
      remainingDice: DieState[],
      path: AtomicMove[]
    ) {
      for (let i = 0; i < remainingDice.length; i++) {
        const die = remainingDice[i];
        if (die.used) {
          continue;
        }

        // All valid moves using this die from the current point
        const validMoves = Rules.findValidMovesForDie(state, die.value).filter(
          (m) => m.from === fromPoint
        );
        for (const move of validMoves) {
          const newMove: AtomicMove = { from: move.from, to: move.to, dieValue: die.value };
          // Append move to path
          const newPath = [...path, newMove];
          paths.push({ moves: newPath });

          // Apply move to cloned state to continue chaining
          const newState = Game.State.applyMove(playerColor, structuredClone(state), newMove);

          // Build list excluding the specific die instance we just used
          const nextRemaining = remainingDice.slice(0, i).concat(remainingDice.slice(i + 1));
          if (nextRemaining.length > 0) {
            findPaths(newState, newMove.to, nextRemaining, newPath);
          }
        }
      }
    }

    findPaths(gameState, selectedPointIndex, unusedDice, []);

    // Derive final destinations
    const destToMinLen: Record<number, number> = {};
    for (const path of paths) {
      const dest = path.moves[path.moves.length - 1].to;
      const len = path.moves.length;
      if (destToMinLen[dest] === undefined || len < destToMinLen[dest]) {
        destToMinLen[dest] = len;
      }
    }

    const singleTargets: number[] = [];
    const multiStepTargets: { first: number; second: number }[] = [];
    Object.entries(destToMinLen).forEach(([destStr, len]) => {
      const dest = Number(destStr);
      singleTargets.push(dest);
      if (len > 1) {
        multiStepTargets.push({ first: -1, second: dest }); // 'first' unused by highlight logic
      }
    });

    setPotentialMovePaths(paths);
    setPotentialMovesSingle(singleTargets);
    setPotentialMovesCombo(multiStepTargets);
  }, [selectedPointIndex, gameState, isPlayerTurn, currentDice, isAnimating]);

  function processNextQueuedMove(currentGameState: GameState | null) {
    if (!currentGameState) return;
    if (pendingMoveQueueRef.current.length === 0) return;

    const nextMove = pendingMoveQueueRef.current[0];

    // Re‑validate against freshest state
    if (!Rules.isMoveValid(currentGameState, nextMove)) {
      pendingMoveQueueRef.current = [];
      return;
    }

    lastAppliedMovesCountRef.current = localTurnHistory.length;

    GameLogger.logDebug("GameBoard", `Attempting move ${lastAppliedMovesCountRef.current}`);

    onAttemptMove(nextMove).then((success) => {
      if (!success) {
        pendingMoveQueueRef.current = [];
        GameLogger.logError("GameBoard", `Move failed: ${JSON.stringify(nextMove)}`);
      }
    });
  }

  // Process pending moves when gameState changes
  useEffect(() => {
    if (!gameState) return;
    if (isAnimating) return;

    GameLogger.logDebug(
      "GameBoard",
      `Processing pending move ${localTurnHistory.length} vs ${lastAppliedMovesCountRef.current}`
    );

    if (localTurnHistory.length > lastAppliedMovesCountRef.current) {
      pendingMoveQueueRef.current.shift();
      processNextQueuedMove(gameState);
    }
  }, [gameState, isAnimating]);

  const attemptPointSelect = async (pointIndex: number) => {
    if (!canSelectChecker()) {
      return;
    }

    if (
      selectedPointIndex !== null &&
      (potentialMovesSingle.includes(pointIndex) ||
        potentialMovesCombo.some((c) => c.second === pointIndex))
    ) {
      // Collect candidate paths ending at this destination
      const candidatePaths = potentialMovePaths.filter(
        (p) => p.moves[p.moves.length - 1].to === pointIndex
      );

      let moveAttempts: AtomicMove[] = [];
      if (candidatePaths.length > 0) {
        // Choose shortest path (fewest dice). Tie-breaker: higher total die value descending.
        candidatePaths.sort((a, b) => {
          if (a.moves.length !== b.moves.length) {
            return a.moves.length - b.moves.length;
          }
          const sumA = a.moves.reduce((s, m) => s + m.dieValue, 0);
          const sumB = b.moves.reduce((s, m) => s + m.dieValue, 0);
          return sumB - sumA;
        });
        moveAttempts = candidatePaths[0].moves;
      }

      setSelectedPointIndex(null);
      resetPotentialMoves();

      if (moveAttempts.length > 0) {
        GameLogger.logInfo("GameBoard", `Attempting path with ${moveAttempts.length} moves`);
        pendingMoveQueueRef.current.push(...moveAttempts);
        processNextQueuedMove(gameState);
      }
    } else if (selectedPointIndex === pointIndex) {
      // Clicked the same selected point again: Deselect
      setSelectedPointIndex(null);
      // potentialMoves will be cleared by the useEffect dependent on selectedPointIndex
    } else if (
      // Prevent selecting a bear-off area as a source point
      pointIndex !== Constants.OFF_INDEX_WHITE &&
      pointIndex !== Constants.OFF_INDEX_BLACK &&
      gameState.board[pointIndex]?.player === gameState.currentPlayerColor &&
      gameState.board[pointIndex]?.count > 0
    ) {
      // Selected a new valid point owned by the current player
      setSelectedPointIndex(pointIndex);
    } else {
      // Clicked an invalid point (empty, opponent's, etc.) when not making a move,
      // or a point not owned by the player for initial selection.
      // This also handles clicking "off" the current selection if not making a valid new selection or re-selecting.
      setSelectedPointIndex(null);
    }
  };

  const attemptBarSelect = () => {
    if (!canSelectChecker()) {
      return;
    }
    // Ensure there are checkers to select from the bar
    if (gameState.currentPlayerColor === Constants.PLAYER_WHITE && whiteBarCount > 0) {
      setSelectedPointIndex(-1);
    } else if (gameState.currentPlayerColor === Constants.PLAYER_BLACK && blackBarCount > 0) {
      setSelectedPointIndex(24);
    }
  };

  return (
    <div
      id={"game-board"}
      className="bg-gray-50 shadow-xl overflow-hidden w-full h-full"
      ref={parentRef}
      style={{
        minHeight: 0
      }}
    >
      {/* Pixi JS elements starting with the board */}
      {stageWidth > 0 && stageHeight > 0 && (
        <Stage
          width={stageWidth}
          height={stageHeight}
          options={{ backgroundColor: 0x00ff00, antialias: true }}
        >
          {/* Board background*/}
          {boardBGTexture && (
            <Sprite
              texture={boardBGTexture}
              anchor={0.5}
              position={[stageWidth / 2, stageHeight / 2]}
              scale={[boardBGScale, boardBGScale]}
              roundPixels={true}
            />
          )}
          {/* Board */}
          {boardTexture && (
            <Sprite
              texture={boardTexture}
              anchor={0.5}
              position={[stageWidth / 2, stageHeight / 2]}
              scale={[boardScale, boardScale]}
              roundPixels={true}
            />
          )}
          {/* Dice - sidebar and overlay */}
          <GameDice
            currentDice={currentDice}
            whiteDice={whiteDice}
            blackDice={blackDice}
            diceRolled={diceRolled}
            playerColor={playerColor}
            currentPlayerColor={gameState.currentPlayerColor}
            playerUsername={
              currentUserId === gameDetails.player1Id
                ? gameDetails.player1Username
                : gameDetails.player2Username
            }
            opponentUsername={
              currentUserId === gameDetails.player1Id
                ? gameDetails.player2Username
                : gameDetails.player1Username
            }
            diceArea={diceArea}
            playArea={playArea}
            boardScale={boardScale}
            shouldShowRollInitial={isDiceRollInitialActive && !gameOver}
            shouldShowRollInterstitial={isDiceRollInterstitialActive && !gameOver}
            overlayInterface={overlay}
            onRollAnimationComplete={() => {
              setIsDiceRollInitialActive(false);
              setIsDiceRollInterstitialActive(false);
            }}
            gameMode={gameMode}
          />
          {/* Doubling Cube - only show if doubling is allowed */}
          {gameDetails.doublingAllowed && (
            <DoublingCubePixi
              cubeValue={gameDetails.cubeValue || 1}
              cubeOwner={gameDetails.cubeOwner || null}
              player1Id={gameDetails.player1Id}
              canOfferDouble={canOfferDouble && !gameOver}
              onOfferDouble={onOfferDouble || (() => {})}
              boardScale={boardScale}
              playArea={{
                x: playArea[0],
                y: playArea[1],
                width: playArea[2],
                height: playArea[3]
              }}
            />
          )}
          {/* Bear Off Area 1 */}
          <GameCheckerOffArea
            gameState={displayGameState}
            offBoardArea={bearOff1Area}
            boardScale={boardScale * 1.2}
            checkerTexture={!isFlipped ? "board-checker-local" : "board-checker-opponent"}
            checkerCount={
              !isFlipped ? displayGameState.off?.white || 0 : displayGameState.off?.black || 0
            }
            checkerDisplayCount={1}
            displayCounter={CounterDisplayType.DisplayedOnTop}
            ownerPlayerColor={!isFlipped ? Constants.PLAYER_WHITE : Constants.PLAYER_BLACK}
            isTop={false}
            highlightForSingle={potentialMovesSingle.includes(
              !isFlipped ? Constants.OFF_INDEX_WHITE : Constants.OFF_INDEX_BLACK
            )}
            highlightForCombo={potentialMovesCombo.some(
              (m) =>
                m.second === (!isFlipped ? Constants.OFF_INDEX_WHITE : Constants.OFF_INDEX_BLACK)
            )}
            overlayInterface={overlay}
            onClick={() =>
              attemptPointSelect(!isFlipped ? Constants.OFF_INDEX_WHITE : Constants.OFF_INDEX_BLACK)
            }
          />
          {/* Bear Off Area 2 */}
          <GameCheckerOffArea
            gameState={displayGameState}
            offBoardArea={bearOff2Area}
            boardScale={boardScale * 1.2}
            checkerTexture={!isFlipped ? "board-checker-opponent" : "board-checker-local"}
            checkerCount={
              !isFlipped ? displayGameState.off?.black || 0 : displayGameState.off?.white || 0
            }
            checkerDisplayCount={1}
            displayCounter={CounterDisplayType.DisplayedOnBottom}
            ownerPlayerColor={!isFlipped ? Constants.PLAYER_BLACK : Constants.PLAYER_WHITE}
            isTop={true}
            highlightForSingle={potentialMovesSingle.includes(
              !isFlipped ? Constants.OFF_INDEX_BLACK : Constants.OFF_INDEX_WHITE
            )}
            highlightForCombo={potentialMovesCombo.some(
              (m) =>
                m.second === (!isFlipped ? Constants.OFF_INDEX_BLACK : Constants.OFF_INDEX_WHITE)
            )}
            overlayInterface={overlay}
            onClick={() =>
              attemptPointSelect(!isFlipped ? Constants.OFF_INDEX_BLACK : Constants.OFF_INDEX_WHITE)
            }
          />
          {/* Bar Area 1 */}
          <GameBoardBar
            gameState={displayGameState}
            selectedPointIndex={selectedPointIndex}
            setSelectedPointIndex={setSelectedPointIndex}
            ownerPlayerColor={!isFlipped ? Constants.PLAYER_WHITE : Constants.PLAYER_BLACK}
            offBoardArea={bar1Area}
            boardScale={boardScale}
            checkerTexture={!isFlipped ? "board-checker-local" : "board-checker-opponent"}
            checkerCount={
              !isFlipped ? displayGameState.bar.white || 0 : displayGameState.bar.black || 0
            }
            checkerDisplayCount={1}
            displayCounter={CounterDisplayType.DisplayedOnBottom}
            overlayInterface={overlay}
            onClick={attemptBarSelect}
          />
          {/* Bar Area 2 */}
          <GameBoardBar
            gameState={displayGameState}
            selectedPointIndex={selectedPointIndex}
            setSelectedPointIndex={setSelectedPointIndex}
            ownerPlayerColor={!isFlipped ? Constants.PLAYER_BLACK : Constants.PLAYER_WHITE}
            offBoardArea={bar2Area}
            boardScale={boardScale}
            checkerTexture={!isFlipped ? "board-checker-opponent" : "board-checker-local"}
            checkerCount={
              !isFlipped ? displayGameState.bar?.black || 0 : displayGameState.bar?.white || 0
            }
            checkerDisplayCount={1}
            displayCounter={CounterDisplayType.DisplayedOnTop}
            overlayInterface={overlay}
            onClick={attemptBarSelect}
          />
          {/* Points */}
          {[11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0].map((index) => (
            <React.Fragment key={index}>
              <GameBoardPoint
                gameState={displayGameState}
                pointIndex={index}
                point={displayGameState.board[index]}
                isTop={isFlipped}
                highlightForSingle={potentialMovesSingle.includes(index)}
                highlightForCombo={potentialMovesCombo.some((m) => m.second === index)}
                isSelected={selectedPointIndex === index}
                pointArea={getPointArea(11 - index, isFlipped)}
                boardScale={boardScale}
                overlayInterface={overlay}
                onClick={() => attemptPointSelect(index)}
              />
            </React.Fragment>
          ))}
          {[23, 22, 21, 20, 19, 18, 17, 16, 15, 14, 13, 12].map((index) => (
            <React.Fragment key={index}>
              <GameBoardPoint
                gameState={displayGameState}
                pointIndex={index}
                point={displayGameState.board[index]}
                isTop={!isFlipped}
                highlightForSingle={potentialMovesSingle.includes(index)}
                highlightForCombo={potentialMovesCombo.some((m) => m.second === index)}
                isSelected={selectedPointIndex === index}
                pointArea={getPointArea(index - 12, !isFlipped)}
                boardScale={boardScale}
                overlayInterface={overlay}
                onClick={() => attemptPointSelect(index)}
              />
            </React.Fragment>
          ))}

          {/* Debug - Draw debug overlays for the board (controlled by BoardConstants.BOARD_DEBUG_RENDERING) */}
          <GameBoardDebugOverlays
            bar1Area={bar1Area}
            bar2Area={bar2Area}
            bearOff1Area={bearOff1Area}
            bearOff2Area={bearOff2Area}
            diceArea={diceArea}
            getPointArea={getPointArea}
            isFlipped={isFlipped}
            stageWidth={stageWidth}
            stageHeight={stageHeight}
          />
        </Stage>
      )}
    </div>
  );
}
